import React from "react";
import { Input, InputNumber, Space, Switch, Typography, Select } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MediaPicker from "../../../components/mediaPicker";
import {
  PanelCard,
  Field,
  FieldGrid,
  ItemBox,
  WrapRow,
  RowActions,
  AddItemButton,
  LinkField,
  listOps,
  newId,
} from "./layoutFormKit";

const { Text } = Typography;

// Field-driven section forms. A section form declares its fields; the kit
// renders them and keeps `data` in the exact shape the customer section reads.
//
// Field: { name, label, type, placeholder?, help?, max?, height?, fields? }
//   text | textarea | richtext (Quill HTML) | media (MediaPicker array) |
//   link ({ type, url }) | video (YouTube, saved as /embed/ URL) | number |
//   switch | list (array of items with `fields`) | group (object with `fields`)

// The site builds the video thumbnail from an /embed/ URL, so watch / youtu.be
// / shorts links are normalised to that form.
export const toYoutubeEmbed = (input = "") => {
  const url = String(input).trim();
  const match =
    url.match(/youtube\.com\/watch\?[^#]*v=([\w-]{6,})/) ||
    url.match(/youtu\.be\/([\w-]{6,})/) ||
    url.match(/youtube\.com\/(?:shorts|embed|live)\/([\w-]{6,})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : url;
};

export const RichTextInput = ({ value, onChange, height = 140 }) => (
  <div style={{ marginBottom: 42 }}>
    <ReactQuill theme="snow" value={value || ""} onChange={onChange} style={{ height }} />
  </div>
);

const emptyValue = (field) => {
  switch (field.type) {
    case "media":
    case "list":
      return [];
    case "group":
      return Object.fromEntries((field.fields || []).map((f) => [f.name, emptyValue(f)]));
    case "switch":
      return Boolean(field.defaultValue);
    case "link":
      return { type: "internal", url: "" };
    case "number":
      return field.defaultValue ?? null;
    default:
      return field.defaultValue ?? "";
  }
};

export const emptyItem = (fields, prefix = "itm") => ({
  id: newId(prefix),
  ...Object.fromEntries(fields.map((f) => [f.name, emptyValue(f)])),
});

const FieldControl = ({ field, value, onChange }) => {
  switch (field.type) {
    case "textarea":
      return (
        <Input.TextArea
          rows={field.rows || 3}
          value={value || ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "richtext":
      return <RichTextInput height={field.height} value={value} onChange={onChange} />;
    case "media":
      return <MediaPicker max={field.max || 1} value={value || []} onChange={onChange} />;
    case "link":
      return <LinkField value={value} onChange={onChange} />;
    case "video":
      return (
        <Input
          value={value || ""}
          placeholder={field.placeholder || "https://www.youtube.com/watch?v=..."}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onChange(toYoutubeEmbed(e.target.value))}
        />
      );
    case "number":
      return (
        <InputNumber
          style={{ width: "100%" }}
          min={field.min}
          max={field.maxValue}
          step={field.step}
          value={value}
          placeholder={field.placeholder}
          onChange={onChange}
        />
      );
    case "switch":
      return <Switch checked={Boolean(value)} onChange={onChange} />;
    case "select":
      return (
        <Select
          style={{ width: "100%" }}
          value={value || field.defaultValue}
          onChange={onChange}
          options={field.options}
        />
      );
    default:
      return (
        <Input
          value={value || ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

const ListControl = ({ field, value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const ops = listOps(items, onChange);
  const inlineMedia = field.fields.filter((f) => f.type === "media");
  const others = field.fields.filter((f) => f.type !== "media");

  return (
    <Space direction="vertical" size={10} style={{ width: "100%" }}>
      {items.map((item, index) => (
        <ItemBox key={item.id || index}>
          <WrapRow align="flex-start" gap={12}>
            {inlineMedia.map((f) => (
              <div style={{ flex: "none" }} key={f.name}>
                {f.label && (
                  <div style={{ marginBottom: 6, fontWeight: 500, fontSize: 13, color: "#4b5563" }}>
                    {f.label}
                  </div>
                )}
                <FieldControl
                  field={f}
                  value={item[f.name]}
                  onChange={(val) => ops.update(index, { [f.name]: val })}
                />
              </div>
            ))}
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
              <FieldsEditor
                fields={others}
                data={item}
                onChange={(next) => ops.update(index, next)}
              />
            </div>
            <RowActions
              index={index}
              total={items.length}
              onMove={ops.move}
              onRemove={ops.remove}
            />
          </WrapRow>
        </ItemBox>
      ))}
      <AddItemButton
        label={`Add ${field.itemLabel || "Item"}`}
        onClick={() => ops.add(emptyItem(field.fields))}
        disabled={field.maxItems ? items.length >= field.maxItems : false}
      />
    </Space>
  );
};

// Renders `fields` against `data`; short inputs share a responsive grid row.
export const FieldsEditor = ({ fields, data = {}, onChange }) => {
  const set = (name, value) => onChange({ ...data, [name]: value });
  const isShort = (f) => ["text", "number", "switch", "link", "video", "select", undefined].includes(f.type);

  const blocks = [];
  let shortRun = [];
  const flush = () => {
    if (shortRun.length) blocks.push({ grid: shortRun });
    shortRun = [];
  };
  fields.forEach((f) => {
    if (isShort(f) && !f.fullWidth) shortRun.push(f);
    else {
      flush();
      blocks.push({ field: f });
    }
  });
  flush();

  const renderField = (f) => {
    const label =
      f.type === "list"
        ? `${f.label} (${(Array.isArray(data[f.name]) ? data[f.name] : []).length})`
        : f.label;
    return (
      <Field label={f.type === "group" ? null : label} key={f.name}>
        {f.type === "list" ? (
          <ListControl field={f} value={data[f.name]} onChange={(val) => set(f.name, val)} />
        ) : f.type === "group" ? (
          <PanelCard icon={f.icon || "folder-2-line"} title={f.label}>
            <FieldsEditor
              fields={f.fields}
              data={data[f.name] || {}}
              onChange={(val) => set(f.name, val)}
            />
          </PanelCard>
        ) : (
          <FieldControl field={f} value={data[f.name]} onChange={(val) => set(f.name, val)} />
        )}
        {f.help && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {f.help}
          </Text>
        )}
      </Field>
    );
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {blocks.map((block, i) =>
        block.grid ? (
          <FieldGrid key={i} min={220}>
            {block.grid.map(renderField)}
          </FieldGrid>
        ) : (
          renderField(block.field)
        ),
      )}
    </Space>
  );
};

// A section form: one panel with the section's fields.
export const SectionForm = ({ icon, title, fields, data = {}, onChange }) => (
  <div style={{ padding: "8px 0" }}>
    <PanelCard icon={icon} title={title}>
      <FieldsEditor fields={fields} data={data} onChange={onChange} />
    </PanelCard>
  </div>
);

// Builds a section form component from a field config. `Form.defaultData()`
// is the empty data a new section starts with.
export const createSectionForm = ({ icon, title, fields }) => {
  const Form = ({ data, onChange }) => (
    <SectionForm icon={icon} title={title} fields={fields} data={data} onChange={onChange} />
  );
  Form.defaultData = () => Object.fromEntries(fields.map((f) => [f.name, emptyValue(f)]));
  return Form;
};

// Sections with no settings (functional pages like Cart or Checkout).
export const NoSettings = ({ text }) => (
  <div style={{ padding: "8px 0" }}>
    <Text type="secondary">
      {text || "This section has no settings. It renders the page's built-in functionality."}
    </Text>
  </div>
);
